/**
 * Service regions, the interpreting languages each one offers, and per-account
 * region grants. One copy for the server and both clients: vrs-server requires
 * it, the web client-profile.js reads it as window.MalkaServiceRegions, native
 * reads it through react/features/interpreter-queue/interpreterLanguages.ts.
 * Before this file every screen kept its own language list (ASL/LSQ in the
 * native VRI console, ASL/LSQ/Spanish/French in native scheduling, the Gulf
 * atoms only on web), and they drifted.
 *
 * A region is where a call is served: its interpreter coverage and languages,
 * and who may call into it. It is not the destination's country code: a VRS
 * call from the US to a foreign number is still served in us-ca.
 *
 * - us-ca: the United States and Canada (MalkaVRS, MalkaVRI, MapleVRI).
 * - gulf: the six GCC countries (MalkaVCS). Kuwait-only is a narrower grant
 *   inside it ('gulf:KW'), not a separate language list.
 *
 * Admins grant regions per account (docs/regional-queue-routing.md). Each tenant
 * also declares a home region (operations.serviceRegion), which is what a
 * client sees before choosing and what an account with one grant is served in.
 *
 * `code` is the atom sent on the wire. The queue match lowercases both sides,
 * but CDRs, queue views and the interpreter report filter compare it as stored,
 * so it is the atom the admin dashboard assigns to interpreters
 * (vrs-admin-dashboard.js LANGUAGE_CATEGORIES): 'KuwaitiSL', not 'KUWAITISL'.
 */
(function(root, factory) {
    const serviceRegions = factory();
    if (typeof module === 'object' && module.exports) module.exports = serviceRegions;
    else root.MalkaServiceRegions = serviceRegions;
})(typeof globalThis !== 'undefined' ? globalThis : this, function() {
    const DEFAULT_REQUEST_LANGUAGE = 'ASL';
    const DEFAULT_SERVICE_REGION = 'us-ca';

    // Bump when the meaning of a grant or of coverage changes; stored with each
    // request so a decision can be read back against the rules it was made under.
    const REGION_POLICY_VERSION = 1;

    // Keyed by the lowercased atom.
    const LANGUAGES = Object.freeze({
        asl: Object.freeze({ code: 'ASL', label: 'ASL', kind: 'sign' }),
        lsq: Object.freeze({ code: 'LSQ', label: 'LSQ (Québec)', kind: 'sign' }),

        // Distinct languages, not dialects of one another or of ASL; see
        // docs/malkavcs-surface.md → Languages.
        kuwaitisl: Object.freeze({ code: 'KuwaitiSL', label: 'Kuwaiti Sign Language', kind: 'sign' }),
        saudisl: Object.freeze({ code: 'SaudiSL', label: 'Saudi Sign Language', kind: 'sign' }),
        arsl: Object.freeze({ code: 'ArSL', label: 'Arabic Sign Language (ArSL)', kind: 'sign' }),

        english: Object.freeze({ code: 'English', label: 'English', kind: 'spoken' }),
        french: Object.freeze({ code: 'French', label: 'French', kind: 'spoken' }),
        spanish: Object.freeze({ code: 'Spanish', label: 'Spanish', kind: 'spoken' }),
        arabic: Object.freeze({ code: 'Arabic', label: 'Arabic', kind: 'spoken' })
    });

    const SERVICE_REGIONS = Object.freeze({
        'us-ca': Object.freeze({
            id: 'us-ca',
            label: 'United States & Canada',
            countries: Object.freeze([ 'US', 'CA' ]),
            countryLabels: Object.freeze({ US: 'United States', CA: 'Canada' }),
            defaultLanguage: 'ASL',
            signLanguages: Object.freeze([ 'ASL', 'LSQ' ]),

            // Spoken-language VRI a client can schedule (English to or from these).
            spokenLanguages: Object.freeze([ 'French', 'Spanish' ])
        }),
        gulf: Object.freeze({
            id: 'gulf',
            label: 'Gulf (GCC)',
            countries: Object.freeze([ 'KW', 'SA', 'AE', 'QA', 'BH', 'OM' ]),
            countryLabels: Object.freeze({
                KW: 'Kuwait', SA: 'Saudi Arabia', AE: 'United Arab Emirates', QA: 'Qatar', BH: 'Bahrain', OM: 'Oman'
            }),
            defaultLanguage: 'ArSL',

            // Arabic sign languages only; ASL is not offered in the Gulf.
            signLanguages: Object.freeze([ 'KuwaitiSL', 'SaudiSL', 'ArSL' ]),

            // What a client is offered by default: ArSL only (Robert,
            // 2026-10-29: "Just ArSL pls for the signed languages by
            // default"). KuwaitiSL and SaudiSL stay known atoms for
            // interpreters, bookings and old requests, but no picker offers
            // them until a tenant pool or this list names them again.
            offeredSignLanguages: Object.freeze([ 'ArSL' ]),

            // No spoken-language scheduling is offered in the Gulf yet.
            spokenLanguages: Object.freeze([])
        })
    });

    function language(value) {
        return typeof value === 'string' ? LANGUAGES[value.trim().toLowerCase()] || null : null;
    }

    function languageLabel(code) {
        const entry = language(code);

        return entry ? entry.label : code;
    }

    function regionById(id) {
        return typeof id === 'string' ? SERVICE_REGIONS[id.trim().toLowerCase()] || null : null;
    }

    /** The tenant's home region (operations.serviceRegion), else us-ca. */
    function serviceRegion(operations) {
        return regionById(operations && operations.serviceRegion) || SERVICE_REGIONS[DEFAULT_SERVICE_REGION];
    }

    // ------------------------------------------------------------------
    // Region grants

    /**
     * `'us-ca'` / `'gulf'` grant a whole region; `'gulf:KW'` grants one member
     * country of it. Anything else is not a grant.
     */
    function parseRegionGrant(value) {
        if (typeof value !== 'string') {
            return null;
        }

        const [ regionPart, countryPart, extra ] = value.trim().split(':');
        const region = regionById(regionPart);

        if (!region || extra !== undefined) {
            return null;
        }
        if (countryPart === undefined) {
            return { region: region.id, country: null };
        }

        const country = countryPart.trim().toUpperCase();

        return region.countries.includes(country) ? { region: region.id, country } : null;
    }

    function formatRegionGrant(grant) {
        return grant.country ? `${grant.region}:${grant.country}` : grant.region;
    }

    /**
     * Valid, canonical, de-duplicated grants in catalog order. A country grant
     * already covered by its whole-region grant is dropped. Returns null for a
     * value that is not a list at all (never designated), so callers can tell
     * "no grants" from "not yet reviewed".
     */
    function normalizeRegionGrants(values) {
        if (!Array.isArray(values)) {
            return null;
        }

        const parsed = values.map(parseRegionGrant).filter(Boolean);
        const whole = new Set(parsed.filter(grant => !grant.country).map(grant => grant.region));
        const out = [];

        for (const region of Object.values(SERVICE_REGIONS)) {
            if (whole.has(region.id)) {
                out.push(region.id);
                continue;
            }
            for (const country of region.countries) {
                if (parsed.some(grant => grant.region === region.id && grant.country === country)) {
                    out.push(`${region.id}:${country}`);
                }
            }
        }

        return out;
    }

    /** The regions an account may be served in, in catalog order. */
    function grantedRegions(grants) {
        const normalized = normalizeRegionGrants(grants) || [];

        return Object.keys(SERVICE_REGIONS)
            .filter(id => normalized.some(grant => parseRegionGrant(grant).region === id));
    }

    /**
     * The member country a client's call in `region` is placed from: set only
     * when their grants for that region are exactly one country (a Kuwait-only
     * client), so Kuwait-only interpreters can serve them. A whole-region grant
     * places the call region-wide.
     */
    function requestCountry(grants, region) {
        const inRegion = (normalizeRegionGrants(grants) || [])
            .map(parseRegionGrant)
            .filter(grant => grant.region === region);

        return inRegion.length === 1 && inRegion[0].country ? inRegion[0].country : null;
    }

    /**
     * Whether an interpreter with `grants` may serve a call in `region`
     * (optionally placed from `country`): a whole-region grant covers every
     * call there; a country grant covers only calls placed from that country.
     */
    function coversRequest(grants, region, country) {
        return (normalizeRegionGrants(grants) || []).some(value => {
            const grant = parseRegionGrant(value);

            return grant.region === region && (!grant.country || grant.country === country);
        });
    }

    /**
     * The region a client's next request is placed in: their saved pick while
     * still granted, else the front door's home region if granted, else the
     * first granted region. Null for an account not yet designated: no choice
     * is shown and the server decides.
     */
    function selectServiceRegion(grants, saved, homeRegionId) {
        const regions = grantedRegions(grants);
        const pick = id => (typeof id === 'string' && regions.includes(id) ? id : null);

        return regions.length ? pick(saved) || pick(homeRegionId) || regions[0] : null;
    }

    /**
     * The one region that offers `code`, or null. The regions share no
     * language, so a request or scheduled session that names KuwaitiSL is a
     * Gulf call and one that names LSQ is a US/Canada call, even from an app
     * that sends no region.
     */
    function regionForLanguage(code) {
        const known = language(code);
        const regions = known
            ? Object.values(SERVICE_REGIONS).filter(region =>
                region.signLanguages.includes(known.code) || region.spokenLanguages.includes(known.code))
            : [];

        return regions.length === 1 ? regions[0].id : null;
    }

    // ------------------------------------------------------------------
    // The Gulf interpreter default (Robert, 2026-11-01: "their languages
    // should have ArSL by default"). Migration 000230 backfills it.

    /** The sign language every Gulf interpreter has by default. */
    const GULF_DEFAULT_SIGN_LANGUAGE = 'ArSL';

    /** The tenants whose home region is the Gulf (whitelabel operations.serviceRegion). */
    const GULF_TENANTS = Object.freeze([ 'malkavcs' ]);

    /**
     * Whether an interpreter serves the Gulf: a Gulf region grant ('gulf' or
     * 'gulf:KW'), a Gulf sign language in their languages, or a Gulf tenant.
     */
    function servesGulf(languages, facts) {
        const grants = facts && facts.grants;
        const tenantId = facts && typeof facts.tenantId === 'string' ? facts.tenantId : '';
        const gulfSign = SERVICE_REGIONS.gulf.signLanguages;

        return (normalizeRegionGrants(grants) || []).some(grant => parseRegionGrant(grant).region === 'gulf')
            || (Array.isArray(languages) && languages.some(code => {
                const known = language(code);

                return Boolean(known && gulfSign.includes(known.code));
            }))
            || GULF_TENANTS.includes(tenantId);
    }

    /**
     * A new Gulf interpreter's languages: ArSL added at the end when it is
     * missing; the other languages and their order stay. Only for an account
     * being created or onboarded. An admin's later edit is kept as it is, so
     * an admin can remove ArSL and it does not come back.
     */
    function withGulfSignLanguage(languages, facts) {
        const list = Array.isArray(languages) ? languages.slice() : [];
        const has = list.some(code => {
            const known = language(code);

            return Boolean(known && known.code === GULF_DEFAULT_SIGN_LANGUAGE);
        });

        return !has && servesGulf(list, facts) ? [ ...list, GULF_DEFAULT_SIGN_LANGUAGE ] : list;
    }

    /** A readable name for a grant: "Gulf (GCC)", "Gulf (GCC): Kuwait". */
    function regionGrantLabel(value) {
        const grant = parseRegionGrant(value);

        if (!grant) {
            return String(value);
        }

        const region = SERVICE_REGIONS[grant.region];

        return grant.country ? `${region.label}: ${region.countryLabels[grant.country]}` : region.label;
    }

    // ------------------------------------------------------------------
    // Languages

    /**
     * The region a language question is about: `regionId` when a
     * multi-region account chose one, else the tenant's home region.
     */
    function regionFor(operations, regionId) {
        return regionById(regionId) || serviceRegion(operations);
    }

    function isHomeRegion(operations, region) {
        return serviceRegion(operations).id === region.id;
    }

    /**
     * The language a request names when the client has not picked one. In the
     * tenant's home region: `operations.defaultRequestLanguage`, else the
     * region's default. In another region the account is granted: that
     * region's default (ASL in us-ca, ArSL in the Gulf). An ASL request
     * never reaches an interpreter who signs only ArSL.
     */
    function defaultRequestLanguage(operations, regionId) {
        const region = regionFor(operations, regionId);
        const declared = operations && operations.defaultRequestLanguage;

        return isHomeRegion(operations, region) && typeof declared === 'string' && declared.trim()
            ? declared.trim()
            : region.defaultLanguage;
    }

    /**
     * The sign languages a client can request for `service` ('vri' or 'vrs'),
     * as `{ value, label }`. In the tenant's home region: the entries of its
     * pool that are sign languages of the region, in pool order. The pool says
     * what the tenant staffs (Malka ASL only, Maple ASL and LSQ); the region
     * says what may be offered there at all. A tenant that declares only one
     * service's pool staffs both from it. In another granted region the tenant
     * pool says nothing, so the region's own list is offered. Never empty.
     */
    function requestLanguageChoices(operations, service, regionId) {
        const region = regionFor(operations, regionId);

        if (!isHomeRegion(operations, region)) {
            return (region.offeredSignLanguages || region.signLanguages).map(code => ({ value: code, label: languageLabel(code) }));
        }

        const pools = (operations && operations.interpreterPools) || {};
        const order = service === 'vrs' ? [ 'vrs', 'vri' ] : [ 'vri', 'vrs' ];
        const declared = (pools[order[0]] && pools[order[0]].languages)
            || (pools[order[1]] && pools[order[1]].languages)
            || [];
        const choices = [];

        for (const entry of Array.isArray(declared) ? declared : []) {
            const known = language(entry);

            if (known && known.kind === 'sign' && region.signLanguages.includes(known.code)
                    && !choices.some(choice => choice.value === known.code)) {
                choices.push({ value: known.code, label: known.label });
            }
        }

        if (choices.length) {
            return choices;
        }

        const fallback = defaultRequestLanguage(operations, regionId);

        return [ { value: fallback, label: languageLabel(fallback) } ];
    }

    /**
     * The language a request sends: the client's saved choice while the list
     * still offers it, else the default, else the first choice. The default
     * outranks list order because a pool may list its languages in any order,
     * but the language a request defaults to is declared once. A saved choice
     * the list no longer offers (another tenant's or region's pick, or a
     * language the tenant dropped) is ignored.
     */
    function selectRequestLanguage(operations, service, saved, regionId) {
        const choices = requestLanguageChoices(operations, service, regionId);
        const offered = value => {
            const key = typeof value === 'string' ? value.trim().toLowerCase() : '';

            return key ? choices.find(choice => choice.value.toLowerCase() === key) : undefined;
        };

        return (offered(saved) || offered(defaultRequestLanguage(operations, regionId)) || choices[0]).value;
    }

    /**
     * The languages a VRI session can be scheduled in: the sign languages a
     * live VRI request offers, then the region's spoken languages. Spoken ones
     * are labelled as such, so "French" is not mistaken for LSQ.
     */
    function scheduleLanguageChoices(operations, regionId) {
        const choices = requestLanguageChoices(operations, 'vri', regionId);

        for (const code of regionFor(operations, regionId).spokenLanguages) {
            const known = language(code);

            if (known && !choices.some(choice => choice.value === known.code)) {
                choices.push({ value: known.code, label: `${known.label} (spoken)` });
            }
        }

        return choices;
    }

    return {
        DEFAULT_REQUEST_LANGUAGE,
        DEFAULT_SERVICE_REGION,
        GULF_DEFAULT_SIGN_LANGUAGE,
        GULF_TENANTS,
        LANGUAGES,
        REGION_POLICY_VERSION,
        SERVICE_REGIONS,
        coversRequest,
        defaultRequestLanguage,
        formatRegionGrant,
        grantedRegions,
        languageLabel,
        normalizeRegionGrants,
        parseRegionGrant,
        regionForLanguage,
        regionGrantLabel,
        requestCountry,
        requestLanguageChoices,
        scheduleLanguageChoices,
        selectRequestLanguage,
        selectServiceRegion,
        serviceRegion,
        servesGulf,
        withGulfSignLanguage
    };
});
